import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { InteractionPhase } from '../lib/interaction';
import type { VoiceBands } from '../lib/pcm-player';
import { playbackRateForTrainSpeed, type TrainSceneState } from '../lib/train-scene';

type TrainCockpitProps = {
  awake: boolean;
  caption: string;
  captionIsUser: boolean;
  phase: InteractionPhase;
  scene: TrainSceneState;
  speaking: boolean;
  voiceBands: VoiceBands;
  routePlaying: boolean;
  routeResetKey: number;
  speedTransitionSeconds: number;
  onRouteTimeUpdate: (currentTime: number, duration: number) => void;
  onRouteError: () => void;
};

type VoiceCellStyle = CSSProperties & { '--voice-cell-level': number };

function playRouteVideo(video: HTMLVideoElement, onRouteError: () => void) {
  video.muted = true;
  void video.play().catch((error: unknown) => {
    // Some browsers defer even muted autoplay until the first user gesture.
    // A pointer/keyboard listener below retries synchronously in that gesture.
    if (
      error instanceof DOMException &&
      (error.name === 'NotAllowedError' || error.name === 'AbortError')
    ) return;
    onRouteError();
  });
}

export function TrainCockpit({
  awake,
  caption,
  captionIsUser,
  phase,
  scene,
  speaking,
  voiceBands,
  routePlaying,
  routeResetKey,
  speedTransitionSeconds,
  onRouteTimeUpdate,
  onRouteError,
}: TrainCockpitProps) {
  const routeVideoRef = useRef<HTMLVideoElement>(null);
  const stoppedForSpeedRef = useRef(scene.trainSpeedKph <= 0);
  const [routeReady, setRouteReady] = useState(false);
  const [routeStopped, setRouteStopped] = useState(scene.trainSpeedKph <= 0);
  const eventId = scene.activeEvent?.id;
  const voiceCellLevels = voiceBands.map((level) => (
    speaking ? Math.max(0, Math.min(1, level)) : 0
  ));

  const publishRouteTime = () => {
    const video = routeVideoRef.current;
    if (!video) return;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    onRouteTimeUpdate(video.currentTime, duration);
  };

  useEffect(() => {
    const video = routeVideoRef.current;
    if (!video) return;
    video.currentTime = 0;
    if (scene.trainSpeedKph > 0) {
      stoppedForSpeedRef.current = false;
      setRouteStopped(false);
    }
    onRouteTimeUpdate(0, Number.isFinite(video.duration) ? video.duration : 0);
  }, [routeResetKey]);

  useEffect(() => {
    const video = routeVideoRef.current;
    if (!video) return;
    if (!routePlaying) {
      video.pause();
      return;
    }
    if (stoppedForSpeedRef.current) return;
    playRouteVideo(video, onRouteError);
  }, [routePlaying, routeResetKey]);

  useEffect(() => {
    const resume = () => {
      const video = routeVideoRef.current;
      if (
        !video ||
        !routePlaying ||
        stoppedForSpeedRef.current ||
        !video.paused
      ) return;
      playRouteVideo(video, onRouteError);
    };
    const resumeWhenVisible = () => {
      if (document.visibilityState === 'visible') resume();
    };
    window.addEventListener('pointerdown', resume, { passive: true });
    window.addEventListener('keydown', resume);
    document.addEventListener('visibilitychange', resumeWhenVisible);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
      document.removeEventListener('visibilitychange', resumeWhenVisible);
    };
  }, [onRouteError, routePlaying]);

  useEffect(() => {
    const video = routeVideoRef.current;
    if (!video) return;
    const requestedRate = playbackRateForTrainSpeed(scene.trainSpeedKph);
    const stopping = requestedRate === 0;
    // Browsers do not treat playbackRate=0 consistently. A true stop is a
    // deceleration to the lowest useful rate followed by pause(); restarting
    // plays from that same media time and ramps upward again.
    const targetRate = stopping ? 0.1 : requestedRate;
    const startRate = video.playbackRate;
    const durationMs = Math.max(0, speedTransitionSeconds) * 1000;
    if (!stopping) {
      stoppedForSpeedRef.current = false;
      setRouteStopped(false);
      if (routePlaying && video.paused) {
        playRouteVideo(video, onRouteError);
      }
    }
    const finishStop = () => {
      if (!stopping) return;
      video.pause();
      stoppedForSpeedRef.current = true;
      setRouteStopped(true);
    };
    if (durationMs === 0 || Math.abs(targetRate - startRate) < 0.002) {
      video.playbackRate = targetRate;
      finishStop();
      return;
    }
    let frame = 0;
    const startedAt = performance.now();
    const advance = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const eased = progress * progress * (3 - 2 * progress);
      video.playbackRate = startRate + (targetRate - startRate) * eased;
      if (progress < 1) frame = window.requestAnimationFrame(advance);
      else finishStop();
    };
    frame = window.requestAnimationFrame(advance);
    return () => window.cancelAnimationFrame(frame);
  }, [onRouteError, routePlaying, scene.trainSpeedKph, speedTransitionSeconds]);

  return (
    <div
      className={`train-cockpit event-${eventId ?? 'none'} weather-${eventId === 'snow' ? 'squall' : 'calm'} ${routeReady ? 'route-ready' : 'route-loading'}`}
      data-route-ready={routeReady ? 'true' : 'false'}
      data-route-stopped={routeStopped ? 'true' : 'false'}
    >
      <div className="cinematic-stage">
        <section className="windshield" aria-label="Snowy mountain railway ahead">
          <div className="outside-world" aria-hidden="true">
            <video
              ref={routeVideoRef}
              className="route-film"
              autoPlay={routePlaying}
              loop
              muted
              playsInline
              preload="auto"
              onCanPlay={() => {
                setRouteReady(true);
                publishRouteTime();
                const video = routeVideoRef.current;
                if (video && routePlaying && !stoppedForSpeedRef.current) {
                  playRouteVideo(video, onRouteError);
                }
              }}
              onPlaying={() => setRouteReady(true)}
              onTimeUpdate={publishRouteTime}
              onError={() => {
                setRouteReady(false);
                onRouteError();
              }}
              aria-hidden="true"
            >
              <source src="/assets/train/northstar-steampunk-route-v2.mp4" type="video/mp4" />
            </video>
            <div className={`track-signal signal-${scene.signalAspect}`}><i /></div>
            <div className="tunnel-mouth"><span /></div>
            <div className="moonwhale"><i /><b /><span /></div>
            <div className="snow-stream snow-stream-far" />
            <div className="snow-stream snow-stream-near" />
            <div className="windshield-haze" />
          </div>
        </section>

        <div
          className={`robot-pilot ${awake ? 'is-awake' : ''} ${speaking ? 'is-speaking' : ''} phase-${phase}`}
          aria-label="Iver, your robot pilot, with a five-cell mechanical voice organ"
        >
          <img
            className="cockpit-master-plate"
            src="/assets/train/northstar-cockpit-v5.png"
            alt="A richly detailed brass robot pilot with five independent acoustic diaphragms built into his jaw, seated beside the co-pilot in a steam locomotive cab"
          />
          <div className="robot-eye-live robot-eye-left" aria-hidden="true" />
          <div className="robot-eye-live robot-eye-right" aria-hidden="true" />
          <div className="robot-voice-organ" aria-hidden="true">
            {voiceCellLevels.map((level, index) => (
              <i
                className={`robot-voice-cell robot-voice-cell-${index + 1}`}
                data-band={index + 1}
                key={index}
                style={{ '--voice-cell-level': level } as VoiceCellStyle}
              />
            ))}
          </div>
        </div>

        <div className="cab-state-plate" aria-label={`${scene.location}. ${Math.round(scene.trainSpeedKph)} kilometers per hour. Signal ${scene.signalAspect}.`}>
          <span>{scene.location}</span>
          <strong>{Math.round(scene.trainSpeedKph)} <small>km/h</small></strong>
          <i className={`signal-${scene.signalAspect}`} />
        </div>
      </div>

      {scene.activeEvent && (
        <div className="event-ticket" role="status" aria-live="polite" key={scene.activeEvent.id}>
          <span>Northstar line</span>
          <strong>{scene.activeEvent.label}</strong>
          <p>{scene.activeEvent.detail}</p>
        </div>
      )}

      <div className={`train-caption ${caption ? 'is-visible' : ''} ${captionIsUser ? 'is-user' : ''}`} aria-live="polite">
        <span>{captionIsUser ? 'Co-pilot' : 'Iver'}</span>
        <p>{caption}</p>
      </div>
    </div>
  );
}
